import { useRef } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router';

import { useModuleTopicUids } from '@/chat/language-switch';
import { LanguageTiles } from '@/components/onboarding/language-tiles';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { useStrings } from '@/i18n/use-strings';
import { track } from '@/lib/analytics';
import { useUserStore } from '@/store/user';

/** `/h/:theme/learn/topics/:slug` — a learner is inside a module. */
const MODULE_PATH = /^\/h\/([^/]+)\/learn\/topics\/[^/]+\/?$/;

/**
 * The header's language picker: the same tile list `/start` shows, in a sheet.
 *
 * Switching is immediate and keeps every completed module — `setLanguage`
 * writes both the store and the language cookie, and the revalidation re-runs
 * the root loader so `<html lang dir>` and every string follow without a
 * reload.
 *
 * A learner switching *inside* a module stays in it. A topic's UID is per
 * language, so the sheet navigates to the same module's topic in the language
 * just chosen — replacing the history entry, because the two URLs are the same
 * page — and the chat replays the learner's saved answers into the new script,
 * landing them at the prompt they left. Only a module with no topic in that
 * language falls back to the journey, as every switch used to.
 */
export function LanguageSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { header, onboarding } = useStrings();
  const { languages, language } = useRootLoaderData();

  const setLanguage = useUserStore((state) => state.setLanguage);
  // Empty on every screen but a module: the module route is what provides it.
  const moduleUids = useModuleTopicUids();

  const { pathname } = useLocation();
  const tiles = useRef<HTMLDivElement>(null);
  const revalidator = useRevalidator();
  const navigate = useNavigate();

  const choose = (code: string) => {
    onOpenChange(false);

    if (code !== language) {
      setLanguage(code);
      track('language_selected', { language: code });
    }

    const insideModule = MODULE_PATH.exec(pathname);

    if (code !== language && insideModule) {
      // Navigating re-runs the loaders, so no separate revalidation is needed.
      const uid = moduleUids[code];

      void navigate(uid ? `/h/${insideModule[1]}/learn/topics/${uid}` : `/h/${insideModule[1]}/learn/topics`, {
        replace: uid !== undefined,
      });

      return;
    }

    void revalidator.revalidate();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="end"
        closeLabel={header.header_menu_close}
        className="gap-0 bg-white"
        // Open on the language already in use, so the current choice is where
        // the keyboard (and the screen reader) starts.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const list = tiles.current;
          const selected = list?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]');

          (selected ?? list?.querySelector<HTMLButtonElement>('button'))?.focus();
        }}
      >
        <SheetHeader>
          <SheetTitle>{header.language_sheet_title}</SheetTitle>
          <SheetDescription>
            {onboarding.language_subtitle} {header.language_sheet_note}
          </SheetDescription>
        </SheetHeader>

        <div ref={tiles} className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <LanguageTiles languages={languages} selected={language} onSelect={choose} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
